"use client";

import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Trans, useTranslation } from "react-i18next";
import { Info, Calendar } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button"; // Added Button import for the info icon trigger

import { getFlatpickrLocale } from "@/utils/flatpickrLocale";
import { useFlatpickrField } from "@/hooks/useFlatpickrField";

interface BirthDateInputProps {
  birthDate: Date | undefined;
  setBirthDate: (date: Date | undefined) => void;
  error: string;
  isLoading: boolean;
  groupRef: React.RefObject<HTMLDivElement>;
  petName: string;
  petType: "cat" | "dog" | null;
  /** Neutral note under the field (e.g. the earliest possible start for a very young pet). */
  hint?: string;
}

const BirthDateInput: React.FC<BirthDateInputProps> = ({
  birthDate,
  setBirthDate,
  error,
  isLoading,
  groupRef,
  petName,
  petType,
  hint,
}) => {
  const { t, i18n } = useTranslation();

  const flatpickrOptions = React.useMemo(() => {
    const today = new Date();
    const minDate = new Date();
    minDate.setFullYear(today.getFullYear() - 25);

    return {
      dateFormat: "d.m.Y",
      altInput: true,
      altFormat: "d.m.Y",
      maxDate: today,
      minDate: minDate,
      locale: getFlatpickrLocale(i18n.language),
      disableMobile: true,
      allowInput: true,
      clickOpens: false,
    };
  }, [i18n.language]);

  const handleFlatpickrChange = (selectedDates: Date[]) => {
    setBirthDate(selectedDates.length > 0 ? selectedDates[0] : undefined);
  };

  const { inputRef, instanceRef } = useFlatpickrField(flatpickrOptions, birthDate, handleFlatpickrChange);

  return (
    <div className="space-y-2 field-row" ref={groupRef}>
      <div className="flex items-center gap-2">
        <Label htmlFor="birth-date">
          {petName ? t("petDetails.birthDate", { petName: petName}) : t("petDetails.birthDateUndefined")}
        </Label>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <Info className="h-5! w-5! text-gray-500" />
              <span className="sr-only">{t("common.moreInfo")}</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("pet-info-infobutton-title-pet-birthdate-explanation")}</DialogTitle>
              <DialogDescription className="whitespace-pre-line">
                <Trans i18nKey="pet-info-pet-birthdate-explanation" components={{ b: <strong /> }} />
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </div>
      <div className="relative">
        <Input
          id="birth-date"
          // Flatpickr hides this original input (`type="hidden"`) and shows a cloned altInput
          // next to it once `useFlatpickrField` initializes. React unconditionally re-asserts an
          // `<input>`'s `type` attribute on every re-render of its fiber (a deliberate browser-bug
          // workaround baked into React itself, independent of whether any prop actually
          // changed) — without declaring it here too, that reassertion kept resetting it back to
          // the default "text" on the very next re-render, unhiding the original next to the
          // altInput. Declaring the same value here makes React's forced reassertion agree with
          // Flatpickr instead of fighting it.
          type="hidden"
          placeholder={t("common.datePlaceholder")}
          disabled={isLoading}
          className={cn(
            "w-full pr-10",
            error && "border-destructive focus-visible:ring-destructive"
          )}
          ref={inputRef}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
          onClick={() => instanceRef.current?.open()}
        >
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="sr-only">
            {t("common.openCalendar")}
          </span>
        </Button>
      </div>
      {error ? <p className="text-sm text-destructive">{error}</p> : hint && <p className="text-xs text-muted-primary">{hint}</p>}
    </div>
  );
};

// `setBirthDate` is intentionally excluded from the comparison: PetDetails passes a fresh
// inline closure on every render, but it always just delegates to the same stable useState
// setters, so treating it as "changed" would only force useFlatpickrField's value-sync effect to
// rerun redundantly (harmless, since it no-ops when the timestamp hasn't changed, but pointless).
//
// `birthDate` is compared by timestamp, not reference, for the same reason: Flatpickr's own
// `allowInput` handling can settle on an equal-but-new Date instance without the calendar day
// actually changing.
const arePropsEqual = (prev: BirthDateInputProps, next: BirthDateInputProps) =>
  prev.birthDate?.getTime() === next.birthDate?.getTime() &&
  prev.error === next.error &&
  prev.isLoading === next.isLoading &&
  prev.groupRef === next.groupRef &&
  prev.petName === next.petName &&
  prev.petType === next.petType &&
  prev.hint === next.hint;

export default React.memo(BirthDateInput, arePropsEqual);
